"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Info,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import "react-pdf/dist/Page/TextLayer.css";
import { Button } from "@ocr-monorepo/ui/components/button";
import { Input } from "@ocr-monorepo/ui/components/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ocr-monorepo/ui/components/popover";
import { ScrollArea } from "@ocr-monorepo/ui/components/scroll-area";
import { Skeleton } from "@ocr-monorepo/ui/components/skeleton";
import { cn } from "@ocr-monorepo/ui/lib/utils";
import { useTRPC } from "@/lib/trpc";
import { useQuery } from "@tanstack/react-query";

const loadReactPdf = () =>
  import("react-pdf").then((mod) => {
    mod.pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${mod.pdfjs.version}/build/pdf.worker.min.mjs`;
    return mod;
  });

const Document = dynamic(() => loadReactPdf().then((mod) => mod.Document), {
  ssr: false,
});
const Page = dynamic(() => loadReactPdf().then((mod) => mod.Page), {
  ssr: false,
});

interface PdfViewerProps {
  id?: string;
  projectSlug: string;
  onClose?: () => void;
  compact?: boolean;
  focusedPages?: number[];
}

const formatFileSize = (bytes?: number | null) => {
  if (!bytes) return "Unknown size";
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = Math.round((bytes / Math.pow(1024, i)) * 100) / 100;
  return `${value} ${sizes[i]}`;
};

const formatUploadDate = (value?: Date | string | null) => {
  if (!value) return "Unknown date";
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleString();
};

const getFileType = (name?: string | null) => {
  if (!name) return "PDF";
  const parts = name.split(".");
  if (parts.length < 2) return "PDF";
  return parts[parts.length - 1].toUpperCase();
};

export function PdfViewer({
  id,
  projectSlug,
  onClose,
  compact = false,
  focusedPages,
}: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const trpc = useTRPC();

  const fileUrl = useMemo(() => (id ? `/api/proxy/${id}` : ""), [id]);

  const { data: metadata, isLoading: isMetaLoading } = useQuery(
    trpc.documents.getDocumentMetadata.queryOptions(
      { projectSlug, documentRecordId: id ?? "" },
      { enabled: !!id && !!projectSlug },
    ),
  );

  useEffect(() => {
    setNumPages(null);
    setPageNumber(1);
    setScale(1);
    setLoadError(null);
  }, [id]);

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === "undefined") return;
    const node = containerRef.current;
    const observer = new ResizeObserver(() => {
      setContainerWidth(node.clientWidth);
    });
    observer.observe(node);
    setContainerWidth(node.clientWidth);
    return () => observer.disconnect();
  }, []);

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber((current) => Math.min(current, numPages));
  };

  const handlePageChange = (value: string) => {
    const next = Number.parseInt(value, 10);
    if (Number.isNaN(next)) return;
    if (!numPages) {
      setPageNumber(Math.max(1, next));
      return;
    }
    setPageNumber(Math.min(Math.max(1, next), numPages));
  };

  const canGoPrev = pageNumber > 1;
  const canGoNext = !!numPages && pageNumber < numPages;
  const documentName = metadata?.name ?? (id ? `Document ${id}` : "Document");
  const visiblePages = focusedPages?.length
    ? focusedPages.filter((page) => page >= 1 && (!numPages || page <= numPages))
    : [pageNumber];

  if (!id) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Select a row to view its PDF file.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-muted/20">
      {compact && (
        <div className="flex min-h-11 shrink-0 items-center gap-4 border-b border-border bg-background px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-[11px] font-semibold">
              {documentName}
            </p>
            <p className="mt-0.5 truncate font-mono text-[9px] text-muted-foreground">
              {getFileType(metadata?.name)} · {formatFileSize(metadata?.file_size)} · {metadata?.page_count ?? numPages ?? "—"} pages
            </p>
          </div>
          <div className="hidden shrink-0 text-right sm:block">
            <p className="text-[8px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
              Uploaded
            </p>
            <p className="mt-0.5 text-[9px] text-foreground">
              {formatUploadDate(metadata?.createdAt)}
            </p>
          </div>
        </div>
      )}
      {!compact && <div className={cn("flex flex-wrap items-center justify-between border-b bg-background/80", compact ? "h-9 gap-1 px-2" : "gap-3 px-4 py-3")}>
        <div className={cn("flex min-w-0 items-center", compact ? "gap-1" : "gap-3")}>
          <div className="min-w-0">
            {!compact && <div className="truncate text-sm font-medium">{documentName}</div>}
            <div className={cn("text-muted-foreground", compact ? "font-mono text-[9px]" : "text-xs")}>
              {numPages ? (compact ? `${pageNumber} / ${numPages}` : `Page ${pageNumber} of ${numPages}`) : "Loading pages"}
            </div>
          </div>
          {!compact && <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="File info">
                <Info />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64">
              <div className="text-sm font-medium">File info</div>
              <div className="mt-3 grid gap-2 text-xs text-muted-foreground">
                <div className="flex items-center justify-between">
                  <span>Type</span>
                  <span className="text-foreground">
                    {getFileType(metadata?.name)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Size</span>
                  <span className="text-foreground">
                    {formatFileSize(metadata?.file_size)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Uploaded</span>
                  <span className="text-foreground">
                    {formatUploadDate(metadata?.createdAt)}
                  </span>
                </div>
              </div>
            </PopoverContent>
          </Popover>}
          {!compact && isMetaLoading && (
            <span className="text-xs text-muted-foreground">
              Loading info...
            </span>
          )}
        </div>

        <div className={cn("flex flex-wrap items-center", compact ? "gap-1" : "gap-2")}>
          <div className={cn("flex items-center", compact ? "gap-1" : "gap-2")}>
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => setPageNumber((value) => Math.max(1, value - 1))}
              disabled={!canGoPrev}
              aria-label="Previous page"
            >
              <ChevronLeft />
            </Button>
            {!compact && <Input
              type="number"
              value={String(pageNumber)}
              onChange={(event) => handlePageChange(event.target.value)}
              className="h-8 w-16 text-center"
              inputMode="numeric"
              min={1}
              max={numPages ?? undefined}
            />}
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() =>
                setPageNumber((value) =>
                  numPages ? Math.min(numPages, value + 1) : value + 1,
                )
              }
              disabled={!canGoNext}
              aria-label="Next page"
            >
              <ChevronRight />
            </Button>
          </div>
          <div className={cn("flex items-center", compact ? "gap-1" : "gap-2")}>
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => setScale((value) => Math.max(0.6, value - 0.1))}
              aria-label="Zoom out"
            >
              <ZoomOut />
            </Button>
            <span className={cn("text-center text-muted-foreground", compact ? "w-9 font-mono text-[9px]" : "w-12 text-xs")}>
              {Math.round(scale * 100)}%
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => setScale((value) => Math.min(2.5, value + 0.1))}
              aria-label="Zoom in"
            >
              <ZoomIn />
            </Button>
          </div>
          {!compact && <Button variant="outline" size="sm" asChild>
            <a href={fileUrl} target="_blank" rel="noreferrer">
              <ExternalLink />
              Open
            </a>
          </Button>}
          {!compact && <Button variant="outline" size="sm" asChild>
            <a href={fileUrl} download={documentName}>
              <Download />
              Download
            </a>
          </Button>}
          {onClose && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
              aria-label="Close document preview"
            >
              <X />
            </Button>
          )}
        </div>
      </div>}

      <div className="group relative min-h-0 flex-1 overflow-hidden bg-muted/20">
        {compact && !focusedPages?.length && (
          <div className="absolute right-6 top-3 z-20 flex items-center gap-1 rounded-md border border-border bg-background/95 p-1 opacity-0 shadow-sm backdrop-blur transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            <Button variant="ghost" size="icon-xs" onClick={() => setPageNumber((value) => Math.max(1, value - 1))} disabled={!canGoPrev} aria-label="Previous page"><ChevronLeft className="size-3" /></Button>
            <span className="min-w-10 text-center font-mono text-[9px] text-muted-foreground">{numPages ? `${pageNumber} / ${numPages}` : "…"}</span>
            <Button variant="ghost" size="icon-xs" onClick={() => setPageNumber((value) => numPages ? Math.min(numPages, value + 1) : value + 1)} disabled={!canGoNext} aria-label="Next page"><ChevronRight className="size-3" /></Button>
            <span className="mx-0.5 h-4 w-px bg-border" />
            <Button variant="ghost" size="icon-xs" onClick={() => setScale((value) => Math.max(0.6, value - 0.1))} aria-label="Zoom out"><ZoomOut className="size-3" /></Button>
            <Button variant="ghost" size="icon-xs" onClick={() => setScale((value) => Math.min(2.5, value + 0.1))} aria-label="Zoom in"><ZoomIn className="size-3" /></Button>
          </div>
        )}
        {loadError ? (
          <div className="flex h-full items-center justify-center text-sm text-destructive">
            {loadError}
          </div>
        ) : (
          <ScrollArea className="h-full" scrollbarOrientation="both">
            <div className={cn("flex h-full w-full justify-center", compact ? "p-4" : "px-4")}>
              <div
                ref={containerRef}
                className={cn(
                  "flex w-full items-center justify-center overflow-hidden",
                  visiblePages.length > 1
                    ? "max-w-[980px] gap-3 bg-transparent"
                    : "max-w-[700px] bg-white",
                  compact && visiblePages.length === 1
                    ? "rounded-md border border-border shadow-sm"
                    : !compact && visiblePages.length === 1
                      ? "rounded-lg shadow-xs"
                      : "",
                )}
              >
                <Document
                  file={fileUrl}
                  onLoadSuccess={handleDocumentLoadSuccess}
                  onLoadError={(error) =>
                    setLoadError(error?.message ?? "Failed to load PDF")
                  }
                  loading={<Skeleton className="h-[400px] w-full" />}
                >
                  <div className="flex w-full items-start justify-center gap-3">
                    {visiblePages.map((visiblePage) => (
                      <div
                        key={visiblePage}
                        className="min-w-0 overflow-hidden rounded-sm border border-border bg-white shadow-sm"
                      >
                        <Page
                          pageNumber={visiblePage}
                          renderAnnotationLayer={false}
                          renderTextLayer={true}
                          width={
                            containerWidth
                              ? Math.max(
                                  240,
                                  (containerWidth -
                                    (visiblePages.length - 1) * 12) /
                                    visiblePages.length,
                                )
                              : undefined
                          }
                          scale={scale}
                        />
                      </div>
                    ))}
                  </div>
                </Document>
              </div>
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
